import React, { PropsWithChildren } from 'react';

import styled from '@emotion/styled';
import * as ReactSelect from 'react-select';
import { variant, ComponentsType } from '@lucid/theme';
import { FieldRenderProps } from 'react-final-form';

import FieldWrapper from '../field-wrapper/field-wrapper';

export interface DefaultDropdownOptionType {
  /** The label to be shown in the Dropdown menu. */
  label: string;
  /** The unique value for the label in the Dropdown menu. */
  value: string;
}

export type DropdownReactSelectProps<T> = ReactSelect.Props<T>;
export type DropdownReactSelectOptionProps<T> = ReactSelect.OptionProps<T>;

export type DropdownProps<T extends Partial<DefaultDropdownOptionType>> = DropdownReactSelectProps<T> & {
  /** Allows passing additional styles to the component. */
  className?: string;
  /** The id that links the form input with the label. */
  id: string;
  /** The available variants for the Dropdown component. */
  variant?: ComponentsType['Form']['Dropdown'];
} & PropsWithChildren<FieldRenderProps<string, HTMLElement, string>>;

const StyledReactSelect = styled(ReactSelect.default)(
  variant({
    scale: 'form.dropdown',
    variants: {
      defaultVariant: {}
    }
  }),
  ({
    hasError,
    required,
    theme
  }) => [
    {
      label: 'reactSelectDropdown'
    },
    required && hasError && {
      '& .reactSelectDropdown__control': {
        border: `1px solid ${theme.colors.pink[60]}`
      }
    }
  ]
);

const Dropdown = <T, >(
  {
    className,
    customErrorMessage,
    id,
    input,
    isDisabled = false,
    isFullWidth = false,
    isHidden = false,
    isRequired = false,
    label,
    meta,
    message,
    variant = 'default',
    ...rest
  }: DropdownProps<T>
) => {
  const hasError = meta?.error && meta?.touched;
  const errorMessage = customErrorMessage || meta?.error;

  return (
    <FieldWrapper
      isDropdown
      className={className}
      customErrorMessage={errorMessage}
      hasError={hasError}
      id={id}
      isDisabled={isDisabled}
      isFullWidth={isFullWidth}
      isHidden={isHidden}
      isRequired={isRequired}
      label={label}
      meta={meta}
    >
      <StyledReactSelect
        {...input}
        {...rest}
        aria-label={id}
        aria-labelledby={id}
        className={className}
        classNamePrefix="reactSelectDropdown"
        hasError={hasError}
        inputId={id}
        required={isRequired}
        variant={variant}
      />
    </FieldWrapper>
  );
};

export { ReactSelect };
export { Dropdown };
export default Dropdown;
